import { useEffect, useState } from "react";
import { masterApi, apiError, PRODUTOS_LABEL } from "@/lib/api";
import Loader, { useMinLoading } from "@/components/Loader";
import { useUnit } from "@/pages/Master/MasterApp";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { normalizaProgressivo } from "@/lib/progressivoConfig";
import { normalizaRotaConfig } from "@/lib/rotaFidelizacao";
import { Link } from "react-router-dom";
import { Save, MapPin, MessagesSquare } from "lucide-react";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

export default function MasterDescontos() {
  const { unitId, unidades, setUnitId } = useUnit();
  const [cfg, setCfg] = useState(null);
  const [precos, setPrecos] = useState([]);
  const [saving, setSaving] = useState(false);

  // Quando "all", o usuário PRECISA escolher uma unidade explícita — não editamos a primeira
  // silenciosamente porque causava confusão (user achava que editava Laçador e estava em Santa Clara).
  const activeUnit = unitId === "all" ? null : unitId;
  const activeUnitNome = unidades.find(u => u.id === activeUnit)?.nome || "";

  const load = async () => {
    if (!activeUnit) { setCfg(null); setPrecos([]); return; }
    try {
      const [{ data: c }, { data: p }] = await Promise.all([
        masterApi.get(`/descontos/config?unit_id=${activeUnit}`),
        masterApi.get(`/precos?unit_id=${activeUnit}`),
      ]);
      setCfg(c); setPrecos(p);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [activeUnit]);
  // Fase 3: aberta pelo link da tela da Rota (#rota-fidelizacao) -> rola até a seção da Rota.
  const cfgCarregada = !!cfg;
  useEffect(() => {
    if (!cfgCarregada || window.location.hash !== "#rota-fidelizacao") return;
    const el = document.getElementById("rota-fidelizacao");
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [cfgCarregada]);
  const showLoader = useMinLoading(!cfg);

  if (!activeUnit) {
    return (
      <div className="px-4 md:px-6 pt-6 pb-8 space-y-4" data-testid="master-descontos-no-unit">
        <div className="card-soft rounded-2xl p-6 max-w-2xl space-y-3"
             style={{ background: "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)", borderLeft: "4px solid #DC2626" }}>
          <h1 className="font-serif-display text-2xl text-rose-900">Selecione uma unidade</h1>
          <p className="text-[13px] text-rose-800">
            Preços e descontos da categoria <b>Normal</b> são salvos <b>por unidade</b>. Para evitar editar a
            unidade errada, escolha qual deseja configurar:
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {unidades.map(u => (
              <button key={u.id} data-testid={`select-unit-${u.id}`}
                      onClick={() => { setUnitId(u.id); }}
                      className="btn-primary-blue h-10 px-4 text-sm font-medium">
                {u.nome}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-neutral-600">
            Você também pode mudar pelo seletor no topo da página a qualquer momento.
          </p>
        </div>
      </div>
    );
  }

  if (showLoader) return <Loader />;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  const save = async () => {
    // Validar preços > 0 antes de salvar
    const invalido = precos.find(p => !(p.preco_bomba > 0) || !(p.preco_cadastro > 0));
    if (invalido) {
      return modal.error(`Preços de "${PRODUTOS_LABEL[invalido.produto]}" inválidos. Bomba e Cadastro devem ser > 0.`);
    }
    // Item 8: progressivo ligado exige mínimo e desconto > 0; os números vão como número (vazio = null).
    const prog = normalizaProgressivo(cfg);
    if (prog.erro) return modal.error(prog.erro);
    // Fase 3: valores da Rota de Fidelização (números; vazio = null, que vale 0).
    const rota = normalizaRotaConfig(cfg);
    if (rota.erro) return modal.error(rota.erro);
    setSaving(true);
    try {
      // Fase 5 (item 7): os textos do agradecimento e do wa.me da indicação moram na central de
      // mensagens. Não vão no salvar: com esta tela aberta, mandar o texto lido antes sobrescreveria
      // o que foi editado em Mensagens automáticas.
      const { agradecimento_mensagem: _txtAgradecimento, indicacao_web_texto: _txtIndicacao, ...semTextos } = cfg;
      await masterApi.put("/descontos/config", { ...semTextos, ...prog.valores, ...rota.valores, unit_id: activeUnit });
      for (const p of precos) {
        await masterApi.put("/precos", { ...p, unit_id: activeUnit });
      }
      modal.success("Configurações salvas");
      load();
    } catch (e) { modal.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const setPrecoField = (prod, field, value) => {
    setPrecos(precos.map(p => p.produto === prod ? { ...p, [field]: Number(value) } : p));
  };

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-descontos">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="chip mb-1.5 text-[10px] py-0.5" style={{ background: "rgba(29,78,216,0.12)", color: "#1D4ED8" }}>
            <MapPin size={11} /> Editando: <b>{activeUnitNome}</b>
          </div>
          <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Descontos & Preços</h1>
        </div>
        <button data-testid="save-descontos-btn" onClick={save} disabled={saving}
                className="btn-action btn-salvar">
          <Save size={15} /> {saving ? "Salvando..." : "Salvar tudo"}
        </button>
      </div>

      <section className="card-soft rounded-xl p-4">
        <h2 className="font-serif-display text-lg mb-2.5">Preços dos Combustíveis</h2>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">Produto</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Preço Bomba</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Preço Cadastro</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {precos.map((p) => (
                <TableRow key={p.produto}>
                  <TableCell className="font-medium">{PRODUTOS_LABEL[p.produto]}</TableCell>
                  <TableCell>
                    <input data-testid={`preco-bomba-${p.produto}`} type="number" step="0.001" value={p.preco_bomba}
                           onChange={(e) => setPrecoField(p.produto, "preco_bomba", e.target.value)}
                           className={inputCls + " w-32"} />
                  </TableCell>
                  <TableCell>
                    <input data-testid={`preco-cadastro-${p.produto}`} type="number" step="0.001" value={p.preco_cadastro}
                           onChange={(e) => setPrecoField(p.produto, "preco_cadastro", e.target.value)}
                           className={inputCls + " w-32"} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <div className="grid md:grid-cols-2 gap-3">
        <DescSection title="Prêmio de Fidelidade" ativo={cfg.fidelidade_ativo} onToggle={(v) => set("fidelidade_ativo", v)} testid="fidelidade">
          <NumField label="Meta (litros)" value={cfg.fidelidade_litros} onChange={(v) => set("fidelidade_litros", v)} testid="fid-litros" />
          <NumField label="Desconto (¢/L)" value={cfg.fidelidade_desconto_centavos} onChange={(v) => set("fidelidade_desconto_centavos", v)} testid="fid-desc" />
        </DescSection>

        <DescSection title="Cliente Novo" ativo={cfg.cliente_novo_ativo} onToggle={(v) => set("cliente_novo_ativo", v)} testid="clinovo">
          <NumField label="Desconto (¢/L)" value={cfg.cliente_novo_desconto_centavos} onChange={(v) => set("cliente_novo_desconto_centavos", v)} testid="cn-desc" />
          <NumField label="Qtd Abastecimentos" value={cfg.cliente_novo_validade_abastecimentos} onChange={(v) => set("cliente_novo_validade_abastecimentos", parseInt(v))} testid="cn-qtd" />
        </DescSection>

        <DescSection title="Cliente Sumido" ativo={cfg.sumido_ativo} onToggle={(v) => set("sumido_ativo", v)} testid="sumido">
          <NumField label="Dias sem abastecer" value={cfg.sumido_dias} onChange={(v) => set("sumido_dias", parseInt(v))} testid="sum-dias" />
          <NumField label="Desconto (¢/L)" value={cfg.sumido_desconto_centavos} onChange={(v) => set("sumido_desconto_centavos", v)} testid="sum-desc" />
        </DescSection>

        <DescSection title="Cliente Frequente" ativo={cfg.frequencia_ativo} onToggle={(v) => set("frequencia_ativo", v)} testid="freq">
          <NumField label="Mín. abastecimentos/mês" value={cfg.frequencia_min_abastecimentos} onChange={(v) => set("frequencia_min_abastecimentos", parseInt(v))} testid="freq-min" />
          <NumField label="Desconto (¢/L)" value={cfg.frequencia_desconto_centavos} onChange={(v) => set("frequencia_desconto_centavos", v)} testid="freq-desc" />
        </DescSection>

        <DescSection title="Promoção do Dia" ativo={cfg.promo_dia_ativo} onToggle={(v) => set("promo_dia_ativo", v)} testid="promodia">
          <NumField label="Desconto (¢/L)" value={cfg.promo_dia_desconto_centavos} onChange={(v) => set("promo_dia_desconto_centavos", v)} testid="pd-desc" />
        </DescSection>

        <DescSection title="Cashback" ativo={cfg.cashback_ativo} onToggle={(v) => set("cashback_ativo", v)} testid="cashback">
          <NumField label="Centavos por litro (¢/L)" value={cfg.cashback_centavos_litro} onChange={(v) => set("cashback_centavos_litro", v)} testid="cb-cents" />
          <NumField label="Dias de validade" value={cfg.cashback_dias_validade} onChange={(v) => set("cashback_dias_validade", parseInt(v))} testid="cb-dias" />
        </DescSection>

        <DescSection title="Oferta com Prazo" ativo={cfg.gamificacao_ativo} onToggle={(v) => set("gamificacao_ativo", v)} testid="gam">
          <NumField label="Desconto (¢/L)" value={cfg.gamificacao_desconto_centavos} onChange={(v) => set("gamificacao_desconto_centavos", v)} testid="gm-desc" />
          <Field label="Data limite">
            <input type="date" data-testid="gm-data" value={(cfg.gamificacao_data_limite || "").slice(0, 10)}
                   onChange={(e) => set("gamificacao_data_limite", e.target.value)} className={inputCls} />
          </Field>
        </DescSection>

        <DescSection title="Abastecimentos Alternados" ativo={cfg.alternados_ativo} onToggle={(v) => set("alternados_ativo", v)} testid="alt">
          <NumField label="Desconto (¢/L)" value={cfg.alternados_desconto_centavos} onChange={(v) => set("alternados_desconto_centavos", v)} testid="alt-desc" />
        </DescSection>

        <DescSection title="Troca de Óleo" ativo={cfg.oleo_ativo} onToggle={(v) => set("oleo_ativo", v)} testid="oleo">
          <NumField label="Meta (litros)" value={cfg.oleo_litros_meta} onChange={(v) => set("oleo_litros_meta", v)} testid="ol-meta" />
          <NumField label="Percentual (%)" value={cfg.oleo_percentual} onChange={(v) => set("oleo_percentual", v)} testid="ol-pct" />
          <NumField label="Validade do prêmio (dias)" value={cfg.oleo_validade_dias ?? 30} onChange={(v) => set("oleo_validade_dias", v)} testid="ol-validade" />
        </DescSection>

        <DescSection title="Mensagem Recompensa" ativo={cfg.recompensa_ativo} onToggle={(v) => set("recompensa_ativo", v)} testid="recomp">
          <NumField label="Litros acumulados" value={cfg.recompensa_litros} onChange={(v) => set("recompensa_litros", v)} testid="rc-litros" />
          <Field label="Texto">
            <textarea data-testid="rc-texto" value={cfg.recompensa_texto} onChange={(e) => set("recompensa_texto", e.target.value)}
                      rows={2}
                      className="w-full rounded-lg bg-white border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition" />
          </Field>
        </DescSection>

        <DescSection title="Oferta Aditivada" ativo={cfg.aditivada_ativo} onToggle={(v) => set("aditivada_ativo", v)} testid="adit">
          <NumField label="Desconto por litro (¢/L)" value={cfg.aditivada_desconto_centavos} onChange={(v) => set("aditivada_desconto_centavos", v)} testid="adit-desc" />
        </DescSection>

        <DescSection title="Indicação" ativo={cfg.indicacao_ativo} onToggle={(v) => set("indicacao_ativo", v)} testid="ind">
          <NumField label="Prêmio (R$)" value={cfg.indicacao_premio_reais} onChange={(v) => set("indicacao_premio_reais", v)} testid="ind-premio" />
          <NumField label="Validade (dias)" value={cfg.indicacao_validade_dias} onChange={(v) => set("indicacao_validade_dias", parseInt(v))} testid="ind-dias" />
        </DescSection>

        <DescSection title="Cupom Saudade" ativo={true} hideToggle testid="saud">
          <NumField label="Desconto (¢/L)" value={cfg.saudade_desconto_centavos} onChange={(v) => set("saudade_desconto_centavos", v)} testid="sd-desc" />
        </DescSection>

        {/* Item 8 (19/10): X ¢/L a partir de R$ Y nesta unidade e categoria. Nasce desligado. */}
        <DescSection title="Desconto Progressivo" ativo={!!cfg.progressivo_ativo} onToggle={(v) => set("progressivo_ativo", v)} testid="progressivo">
          <NumField label="Valor mínimo do abastecimento (R$)" value={cfg.progressivo_valor_minimo_reais} onChange={(v) => set("progressivo_valor_minimo_reais", v)} testid="prog-min" />
          <NumField label="Desconto (¢/L)" value={cfg.progressivo_desconto_centavos} onChange={(v) => set("progressivo_desconto_centavos", v)} testid="prog-desc" />
          <p className="text-[10.5px] text-neutral-500 leading-snug">
            Aparece na pista quando o abastecimento for de pelo menos o valor mínimo: no modo R$ vale o valor digitado; no modo
            litros, litros x preço de cadastro. Concorre com os outros descontos (vale um só) e soma com a troca para Aditivada e o grupo cumulativo.
          </p>
        </DescSection>

        {/* Fase 3 (19/10, item 10): Rota de Fidelização nesta unidade e categoria. O liga/desliga é
            global, na tela Gestão > Rota de Fidelização. Vazio = 0. */}
        <DescSection title="Rota de Fidelização" ativo={true} hideToggle testid="rota" id="rota-fidelizacao">
          <div className="grid grid-cols-3 gap-2">
            <NumField label="Etapa 1 (¢/L)" value={cfg.rota_etapa1_centavos} onChange={(v) => set("rota_etapa1_centavos", v)} testid="rota-etapa1" />
            <NumField label="Etapa 2 (¢/L)" value={cfg.rota_etapa2_centavos} onChange={(v) => set("rota_etapa2_centavos", v)} testid="rota-etapa2" />
            <NumField label="Etapa 3 (¢/L)" value={cfg.rota_etapa3_centavos} onChange={(v) => set("rota_etapa3_centavos", v)} testid="rota-etapa3" />
          </div>
          <NumField label="Prêmio ao concluir (R$)" value={cfg.rota_premio_reais} onChange={(v) => set("rota_premio_reais", v)} testid="rota-premio" />
          <NumField label="Valor mínimo para avançar (R$)" value={cfg.rota_valor_minimo_reais} onChange={(v) => set("rota_valor_minimo_reais", v)} testid="rota-minimo" />
          <p className="text-[10.5px] text-neutral-500 leading-snug">
            Nos 3 primeiros abastecimentos (1 por dia) o cliente recebe só o desconto da etapa, a mais do preço da categoria.
            Abaixo do mínimo recebe o desconto mas não avança. Ao completar ganha o prêmio em saldo (R$), sem somar com
            outros descontos. Sem nenhum valor (etapas e prêmio em 0) a Rota não vale nesta unidade e categoria.
            Liga/desliga em Gestão &gt; Rota de Fidelização.
          </p>
        </DescSection>
      </div>

      <section className="card-soft rounded-xl p-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <h2 className="font-serif-display text-lg">Agradecimento Automático (D+1)</h2>
          <Switch data-testid="toggle-agradecimento" checked={cfg.agradecimento_ativo || false} onCheckedChange={(v) => set("agradecimento_ativo", v)} />
        </div>
        <p className="text-[11px] text-neutral-600">
          Envia WhatsApp no dia seguinte ao abastecimento, no horário desta unidade. Para Motorista de App, o
          liga/desliga fica na tela Motorista de App (padrão: segue este).
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Hora">
            <input type="number" min={0} max={23} data-testid="agradecimento-hora" value={cfg.agradecimento_hora ?? 9}
                   onChange={(e) => set("agradecimento_hora", parseInt(e.target.value) || 0)} className={inputCls} />
          </Field>
          <Field label="Minuto">
            <input type="number" min={0} max={59} data-testid="agradecimento-minuto" value={cfg.agradecimento_minuto ?? 0}
                   onChange={(e) => set("agradecimento_minuto", parseInt(e.target.value) || 0)} className={inputCls} />
          </Field>
        </div>
        {/* Fase 5 (19/10, item 7): o texto saiu daqui para a central de mensagens (padrão do sistema e
            texto por unidade, com o texto já salvo de cada unidade migrado). */}
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-neutral-300 bg-white/60 px-3 py-2"
             data-testid="agradecimento-msg-central">
          <span className="text-[11px] text-neutral-600 flex-1 min-w-[180px]">
            O texto da mensagem fica em Sistema &gt; Mensagens automáticas.
          </span>
          <Link to={`/master/mensagens?unidade=${encodeURIComponent(activeUnit)}&chave=agradecimento`} data-testid="agradecimento-msg-link"
                className="btn-ghost h-8 px-3 text-[11.5px] font-medium inline-flex items-center gap-1.5" style={{ borderRadius: 8 }}>
            <MessagesSquare size={12} /> Editar o texto
          </Link>
        </div>
      </section>

      <section className="card-soft rounded-xl p-4">
        <h2 className="font-serif-display text-lg mb-2">Mensagem do Cupom (Pós-Atendimento)</h2>
        <textarea data-testid="mensagem-cupom" value={cfg.mensagem_cupom || ""} onChange={(e) => set("mensagem_cupom", e.target.value)}
                  rows={3} placeholder="Deixe vazio para pular esta tela."
                  className="w-full rounded-lg bg-white border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition" />
      </section>

      <section className="card-soft rounded-xl p-4 space-y-2.5">
        <h2 className="font-serif-display text-lg">Controle de Acesso</h2>
        {/* v2.27: toggle "Exigir verificação OTP (WhatsApp)" removido — a identificação agora é
            placa + 4 dígitos (ver Configuração › Identificação e anti-fraude). */}
        <ToggleRow testid="acesso-sem-ativo" label="Permitir 'Acesso Sem Celular'" hint="Cliente pode acessar sem código limitado X vezes/mês."
                   checked={cfg.acesso_sem_celular_ativo} onChange={(v) => set("acesso_sem_celular_ativo", v)} />
        <NumField label="Limite mensal 'sem celular'" value={cfg.acesso_sem_celular_limite_mensal}
                  onChange={(v) => set("acesso_sem_celular_limite_mensal", parseInt(v))} testid="acesso-sem-limite" />
      </section>
    </div>
  );
}

function DescSection({ title, ativo, onToggle, hideToggle, children, testid, id }) {
  return (
    <section id={id} className="card-soft rounded-xl p-3.5 space-y-2.5 scroll-mt-24" data-testid={`desc-section-${testid}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-serif-display text-base leading-tight">{title}</h3>
        {!hideToggle && <Switch data-testid={`desc-toggle-${testid}`} checked={ativo} onCheckedChange={onToggle} />}
      </div>
      <div className="space-y-2" style={{ opacity: ativo || hideToggle ? 1 : 0.45 }}>
        {children}
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  const inputCls2 = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";
  return (
    <Field label={label}>
      <input type="number" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
             data-testid={`numfield-${testid}`} className={inputCls2} />
    </Field>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-100 last:border-0 py-2">
      <div>
        <div className="text-[12px] font-medium">{label}</div>
        {hint && <div className="text-[10px] text-neutral-500 mt-0.5">{hint}</div>}
      </div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
